import { useState } from 'react';
import InlineConfirm from '../../../components/InlineConfirm';
import type { ServerSetting } from '../../../services/adminServerSettings';
import { ImageSource, KnownImage, chooseImageTag } from '../../../services/adminUnits';
import { errorMessage } from '../adminErrors';
import CurrentImageList from './CurrentImageList';
import { formatMoment, plural } from './unitsFormat';
import { currentImageTag } from './unitsPolicy';

const RecordedByLabels: Record<ImageSource, string> = {
  Deploy: 'the deploy',
  ManualDeploy: 'a manual deploy',
  BuildWorkflow: 'the image build workflow',
  Admin: 'an admin',
  Manager: 'the unit manager',
};

const questionFor = (tag: string) => `Use image ${tag}? Units on other images finish their job and end.`;

interface UseImageProps {
  tag: string;
  label?: string;
  disabled: boolean;
  onUse: (tag: string) => void;
}

/** The button that uses an image, after the question on the page. */
function UseImage({ tag, label = 'Use', disabled, onUse }: UseImageProps) {
  const [confirming, setConfirming] = useState(false);
  return confirming ? (
    <InlineConfirm
      question={questionFor(tag)}
      confirmLabel="Yes, use it"
      onConfirm={() => {
        setConfirming(false);
        onUse(tag);
      }}
      onCancel={() => setConfirming(false)}
    />
  ) : (
    <button type="button" className="btn sm" disabled={disabled} onClick={() => setConfirming(true)}>
      {label}
    </button>
  );
}

function ImageRow({ image, current, canUse, onUse }: { image: KnownImage; current: boolean; canUse: boolean; onUse: (tag: string) => void }) {
  return (
    <div role="listitem" aria-label={image.tag} className="card" style={{ padding: '12px 14px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: 'anywhere' }}>{image.tag}</span>
          {current && <span className="chip ok"><span className="dot" />Current</span>}
        </div>
        <div className="muted" style={{ fontSize: 12.5 }}>
          Recorded by {RecordedByLabels[image.recordedBy]} on {formatMoment(image.firstRecordedAt)}
          {image.lastWrittenAt && ` · last chosen ${formatMoment(image.lastWrittenAt)}`}
        </div>
        <div style={{ fontSize: 12.5 }}>
          {image.lastWorkedAt ? `Last job done ${formatMoment(image.lastWorkedAt)}` : 'No job done yet'}
          <span className="muted"> · {plural(image.unitsThatRanIt, 'unit ran it', 'units ran it')}</span>
        </div>
      </div>
      <UseImage tag={image.tag} disabled={current || !canUse} onUse={onUse} />
    </div>
  );
}

interface KnownImagesProps {
  images: KnownImage[];
  /** Null until loaded; the current image tag comes from them. */
  settings: ServerSetting[] | null;
  onUsed: (saved: ServerSetting) => void;
}

export default function KnownImages({ images, settings, onUsed }: KnownImagesProps) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = currentImageTag(settings);
  const canUse = settings != null && !saving;

  const use = async (tag: string) => {
    setSaving(true);
    setError(null);
    try {
      onUsed(await chooseImageTag(tag));
      setDraft('');
    } catch (err) {
      setError(errorMessage(err, 'Could not set the image tag.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
      {settings && (
        <div>
          {current ? (
            <>New units start from <span className="mono">{current}</span>.</>
          ) : (
            <span className="chip warn"><span className="dot" />No image tag: no new units.</span>
          )}
        </div>
      )}
      {images.length === 0 ? (
        <div className="card quiet" style={{ padding: 16 }}>
          <span className="muted">No image is recorded yet.</span>
        </div>
      ) : (
        <CurrentImageList
          items={images}
          currentTag={current}
          tagOf={(image) => image.tag}
          renderItem={(image) => (
            <ImageRow key={image.tag} image={image} current={image.tag === current} canUse={canUse} onUse={(tag) => void use(tag)} />
          )}
          label="Known images"
          olderLabel="Older images"
        />
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Another tag of pipelineworker"
          aria-label="Add a tag"
          style={{ height: 36, maxWidth: 420, padding: '0 12px' }}
        />
        <UseImage tag={draft.trim()} label="Add and use" disabled={!draft.trim() || !canUse} onUse={(tag) => void use(tag)} />
      </div>
      {error && <div className="field"><div className="err">{error}</div></div>}
    </div>
  );
}
